// src/components/CommunityTopBar.jsx
import React, { useMemo } from "react";
import { Search, Users, Dot } from "lucide-react";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * CommunityTopBar (clean + modern)
 * - Pills moved OUT of the search shell (no “pill inside search bar” look)
 * - “Community” label matches your Discover micro-label style
 * - Removed “Threads • Reviews • ArtHouse”
 * - Active-now dot uses emerald/green (not cyan)
 * - Keeps subtle cinematic focus + no inner input artifact
 */
export default function CommunityTopBar({
  q,
  setQ,
  activeMode,
  setActiveMode,
  activeNowCount = 0,
  onlineFriendsCount = 0,
}) {
  const modes = useMemo(
    () => [
      { id: "foryou", label: "For You" },
      { id: "mine", label: "Mine" },
    ],
    []
  );

  const pillClass = (active) =>
    cx(
      "h-10 px-6 rounded-full border transition",
      "backdrop-blur-md",
      "text-[11px] tracking-[0.24em] uppercase",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/20",
      active
        ? "border-white/20 bg-white/[0.06] text-cyan-400 shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_14px_40px_rgba(0,0,0,0.38)]"
        : "border-white/10 bg-white/[0.03] text-white/65 hover:text-white/85 hover:bg-white/[0.05]"
    );

  return (
    <div className="w-full">
      {/* Top: label + stats */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* matches your Discover style */}
          <p className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-white/50">
            Community
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-white/55">
            <span className="inline-flex items-center gap-1.5">
              <Dot className="w-5 h-5 text-emerald-300/70" />
              <span className="text-white/70">{activeNowCount}</span> active now
            </span>

            <span className="inline-flex items-center gap-1.5">
              <Users className="w-4 h-4 text-white/45" />
              <span className="text-white/70">{onlineFriendsCount}</span>{" "}
              friends online
            </span>
          </div>
        </div>
      </div>

      {/* Controls: search left, pills right (desktop) */}
      <div className="mt-4 flex items-center gap-3">
        {/* Search */}
        <div
          className={cx(
            "group flex-1 flex items-center gap-2 rounded-full h-11 px-4",
            "border border-white/10 bg-white/[0.03] backdrop-blur-md",
            "shadow-[0_0_0_1px_rgba(255,255,255,0.02)]",
            "transition",
            "focus-within:border-white/15 focus-within:bg-white/[0.04]"
          )}
        >
          <Search className="w-4 h-4 text-white/40 shrink-0" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            aria-label="Search community"
            className={cx(
              "flex-1 min-w-0",
              "bg-transparent",
              "text-[13px] text-white/85 placeholder:text-white/35",
              "outline-none border-none ring-0 focus:ring-0 focus:outline-none focus-visible:ring-0",
              "caret-emerald-200",
              "selection:bg-emerald-300/15 selection:text-white"
            )}
            style={{
              WebkitAppearance: "none",
              WebkitTapHighlightColor: "transparent",
              backgroundColor: "transparent",
              boxShadow: "none",
            }}
          />
        </div>

        {/* Desktop pills (outside search) */}
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setActiveMode(m.id)}
              className={pillClass(activeMode === m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile pills below */}
      <div className="mt-3 sm:hidden flex items-center gap-2">
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setActiveMode(m.id)}
            className={pillClass(activeMode === m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}
